
import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface RateLimitCheck {
  isAllowed: boolean;
  remainingAttempts: number;
  resetTime?: Date;
}

export const useSecureAuth = () => {
  const [rateLimitStatus, setRateLimitStatus] = useState<RateLimitCheck>({
    isAllowed: true,
    remainingAttempts: 15
  });
  const { toast } = useToast();

  const checkRateLimit = useCallback(async (endpoint: string): Promise<boolean> => {
    try {
      // Get client IP (in production, this would be handled server-side)
      const clientIdentifier = `user_${Date.now()}_${Math.random()}`;
      
      // Check if rate limit allows this request
      const { data, error } = await supabase.rpc('check_rate_limit', {
        client_ip: '127.0.0.1', // This would be actual IP in production
        endpoint_name: endpoint,
        max_attempts: 15,
        window_minutes: 15
      });

      if (error) {
        console.error('Rate limit check error:', error);
        return true; // Allow on error to avoid blocking legitimate users
      }

      if (!data) {
        setRateLimitStatus({ isAllowed: false, remainingAttempts: 0 });
        toast({
          title: "Չափազանց շատ փորձություններ",
          description: "Խնդրում ենք սպասել 15 րոպե նոր փորձելուց առաջ",
          variant: "destructive"
        });
        return false;
      }

      return true;
    } catch (error) {
      console.error('Rate limit check failed:', error);
      return true; // Allow on error
    }
  }, [toast]);

  const secureSignIn = useCallback(async (email: string, password: string) => {
    // Check rate limit before attempting sign in
    const isAllowed = await checkRateLimit('login');
    if (!isAllowed) {
      return { error: { message: 'Rate limit exceeded' } };
    }

    // Validate input
    if (!email || !password) {
      toast({
        title: "Սխալ",
        description: "Էլ․ փոստը և գաղտնաբառը պարտադիր են",
        variant: "destructive"
      });
      return { error: { message: 'Invalid input' } };
    }

    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast({
        title: "Սխալ",
        description: "Էլ․ փոստի ֆորմատը սխալ է",
        variant: "destructive"
      });
      return { error: { message: 'Invalid email format' } };
    }

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: email.trim().toLowerCase(),
        password,
      });
      return { error };
    } catch (error) {
      console.error('Sign in error:', error);
      return { error };
    }
  }, [checkRateLimit, toast]);

  const secureSignUp = useCallback(async (email: string, password: string) => {
    // Check rate limit
    const isAllowed = await checkRateLimit('signup');
    if (!isAllowed) {
      return { error: { message: 'Rate limit exceeded' } };
    }

    // Validate password strength - allow any special character
    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecialChar = /[^A-Za-z0-9]/.test(password);
    const hasMinLength = password.length >= 8;

    if (!hasMinLength || !hasUppercase || !hasLowercase || !hasNumber || !hasSpecialChar) {
      toast({
        title: "Սdelays",
        description: "Գdelays\u0576\u0561\u0562\u0561\u057C\u0568 \u057A\u0565\u057F\u0584 \u0567 \u057A\u0561\u0580\u0578\u0582\u0576\u0561\u056F\u056B \u0574\u0565\u056E\u0561\u057F\u0561\u057C, \u0583\u0578\u0584\u0580\u0561\u057F\u0561\u057C, \u0569\u056B\u057E \u0587 \u0570\u0561\u057F\u0578\u0582\u056F \u0576\u056B\u0577, \u0587 \u056C\u056B\u0576\u056B \u0576\u057E\u0561\u0566\u0561\u0563\u0578\u0582\u0575\u0576\u0568 8 \u0576\u056B\u0577",
        variant: "destructive"
      });
      return { error: { message: 'Weak password' } };
    }

    const redirectUrl = `${window.location.origin}/`;
    
    try {
      const { data, error } = await supabase.auth.signUp({
        email: email.trim().toLowerCase(),
        password,
        options: {
          emailRedirectTo: redirectUrl
        }
      });

      return { data, error };
    } catch (error) {
      console.error('Sign up error:', error);
      return { error };
    }
  }, [checkRateLimit, toast]);

  const secureResetPassword = useCallback(async (email: string) => {
    // Check rate limit
    const isAllowed = await checkRateLimit('reset_password');
    if (!isAllowed) {
      return { error: { message: 'Rate limit exceeded' } };
    }

    console.log('Password reset attempt started for email:', email);
    
    try {
      const redirectUrl = `${window.location.origin}/reset-password`;
      console.log('Using redirect URL:', redirectUrl);
      
      const { error } = await supabase.auth.resetPasswordForEmail(email.trim().toLowerCase(), {
        redirectTo: redirectUrl
      });
      
      console.log('Supabase resetPasswordForEmail response:', { error });
      
      if (!error) {
        console.log('Password reset email sent successfully');
        toast({
          title: "Հաջողություն",
          description: "Գաղտնաբառի վերականգնման հղումը ուղարկվել է ձեր էլ․ փոստին",
        });
      } else {
        console.error('Password reset failed with error:', error);
        
        let errorMessage = "Անսպասելի սխալ";
        if (error.message?.includes('Invalid email')) {
          errorMessage = "Սխալ էլ․ փոստի հասցե";
        } else if (error.message?.includes('rate limit')) {
          errorMessage = "Չափազանց շատ հարցումներ: Փորձեք մի փոքր ուշ";
        } else if (error.message?.includes('Email not confirmed')) {
          errorMessage = "Էլ․ փոստը չի հաստատված";
        } else if (error.message?.includes('User not found')) {
          errorMessage = "Այս էլ․ փոստով օգտվող գտնված չէ";
        }
        
        toast({
          title: "Սխալ", 
          description: errorMessage,
          variant: "destructive"
        });
      }
      
      return { error };
    } catch (error) {
      console.error('Unexpected password reset error:', error);
      toast({
        title: "Սխալ",
        description: "Կապակցման խնդիր: Ստուգեք ձեր ինտերնետ կապը",
        variant: "destructive"
      });
      return { error };
    }
  }, [checkRateLimit, toast]);

  return {
    secureSignIn,
    secureSignUp,
    secureResetPassword,
    rateLimitStatus
  };
};
